'use client';

import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLineNewcreator } from '@/app/components/icons/BaseLineNewcreator';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { SearchInput } from '@/app/components/shared/SearchInput';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { cn } from '@/app/utils';
import { MAX_TREE_DEPTH } from '../_constants';
import type { FileFilters } from '../_hooks/use-file-filters';
import { createFileItem, fileStateUpdaters } from '../_utils';
import { fetchCreateFolder } from '../apis';
import type { FileItem } from '../types';
import { FileFilterBar } from './FileFilterBar';

interface CreatorFilterOption {
  key: string;
  label: string;
  searchLabel?: string;
}

interface FileToolbarProps {
  filters: FileFilters;
  onFiltersChange: (updates: Partial<FileFilters>) => void;
  isTeamSpace: boolean;
  creatorFilterOptions: CreatorFilterOption[];
  onSearch: (search: string) => void;
  // 文件状态更新回调
  onFilesUpdate?: (updater: (files: FileItem[]) => FileItem[]) => void;
  className?: string;
  currentDepth: number;
  currentFolderId: string;
  defaultSearchValue?: string;
}

export const FileToolbar: FC<FileToolbarProps> = ({
  filters,
  onFiltersChange,
  isTeamSpace,
  creatorFilterOptions,
  onSearch,
  onFilesUpdate,
  className,
  currentDepth,
  currentFolderId,
  defaultSearchValue,
}) => {
  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const { run: createFolder, loading: creatingFolder } = useFetch(
    () =>
      fetchCreateFolder({
        body: {
          parent_id: currentFolderId,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          if (error?.reason === 'DepthOverLimit') {
            openConfirmDialog({
              title: t('creations.moveDialog.levelLimit'),
              description: t('creations.moveDialog.levelLimitDescription', { limit: MAX_TREE_DEPTH }),
              primaryButtonText: t('common.dialog.gotIt'),
            });
          } else {
            toast({
              message: error?.message,
              type: 'error',
            });
          }
          return;
        }

        const newFolderItem = createFileItem(data.folder);

        toast({
          message: t('creations.folders.newFolderSuccess'),
          type: 'success',
        });

        // 更新
        if (onFilesUpdate) {
          onFilesUpdate((files) => fileStateUpdaters.prependItems(files, [newFolderItem]));
        }
      },
    },
  );

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <CustomTooltip
        content={
          currentDepth >= MAX_TREE_DEPTH && t('creations.moveDialog.levelLimitDescription', { limit: MAX_TREE_DEPTH })
        }
      >
        <Button
          className="flex h-8 items-center gap-2 rounded-8 bg-system-white10 px-4 font-normal text-sm"
          onClick={createFolder}
          disabled={currentDepth >= MAX_TREE_DEPTH || creatingFolder}
        >
          {creatingFolder ? (
            <BaseLineLoading2 className="animate-spin text-xl" />
          ) : (
            <BaseLineNewcreator className="text-xl" />
          )}
          {t('creations.folders.newFolder')}
        </Button>
      </CustomTooltip>

      {/* 分割线 */}
      <span className="text-system-white24 text-xl">|</span>

      {/* Filters */}
      <FileFilterBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        isTeamSpace={isTeamSpace}
        creatorFilterOptions={creatorFilterOptions}
      />

      {/* 搜索框 */}
      <SearchInput
        defaultValue={defaultSearchValue}
        onSearch={onSearch}
        placeholder={t('creations.search.placeholder')}
      />
    </div>
  );
};
